type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  error?: Error;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private static instance: Logger;
  private userId?: string;
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private createLogEntry(level: LogLevel, message: string, context?: Record<string, any>, error?: Error): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      error,
      userId: this.userId,
      sessionId: this.sessionId,
    };
  }

  private log(entry: LogEntry) {
    // Console logging with appropriate level
    const logMessage = `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}`;
    
    switch (entry.level) {
      case 'debug':
        console.debug(logMessage, entry.context, entry.error);
        break;
      case 'info':
        console.info(logMessage, entry.context, entry.error);
        break;
      case 'warn':
        console.warn(logMessage, entry.context, entry.error);
        break;
      case 'error':
        console.error(logMessage, entry.context, entry.error);
        break;
    }

    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(entry);
    }
  }

  private async sendToMonitoring(entry: LogEntry) {
    // TODO: Implement with your monitoring service (Sentry, LogRocket, etc.)
    try {
      // Example with fetch to your logging endpoint
      if (import.meta.env.VITE_LOG_ENDPOINT) {
        await fetch(import.meta.env.VITE_LOG_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entry),
        });
      }
    } catch (error) {
      console.error('Failed to send log to monitoring service:', error);
    }
  }

  debug(message: string, context?: Record<string, any>) {
    this.log(this.createLogEntry('debug', message, context));
  }

  info(message: string, context?: Record<string, any>) {
    this.log(this.createLogEntry('info', message, context));
  }

  warn(message: string, context?: Record<string, any>) {
    this.log(this.createLogEntry('warn', message, context));
  }

  error(message: string, context?: Record<string, any>, error?: Error) {
    this.log(this.createLogEntry('error', message, context, error));
  }

  // Specialized logging methods
  userAction(action: string, context?: Record<string, any>) {
    this.info(`User action: ${action}`, { ...context, type: 'user_action' });
  }

  apiCall(method: string, url: string, status?: number, duration?: number) {
    this.info(`API call: ${method} ${url}`, {
      type: 'api_call',
      method,
      url,
      status,
      duration,
    });
  }

  performance(metric: string, value: number, context?: Record<string, any>) {
    this.info(`Performance: ${metric} = ${value}ms`, {
      type: 'performance',
      metric,
      value,
      ...context,
    });
  }

  security(event: string, context?: Record<string, any>) {
    this.warn(`Security event: ${event}`, {
      type: 'security',
      event,
      ...context,
    });
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience exports
export const logDebug = (message: string, context?: Record<string, any>) => logger.debug(message, context);
export const logInfo = (message: string, context?: Record<string, any>) => logger.info(message, context);
export const logWarn = (message: string, context?: Record<string, any>) => logger.warn(message, context);
export const logError = (message: string, context?: Record<string, any>, error?: Error) => logger.error(message, context, error);
export const logUserAction = (action: string, context?: Record<string, any>) => logger.userAction(action, context);
export const logApiCall = (method: string, url: string, status?: number, duration?: number) => logger.apiCall(method, url, status, duration);
export const logPerformance = (metric: string, value: number, context?: Record<string, any>) => logger.performance(metric, value, context);
export const logSecurity = (event: string, context?: Record<string, any>) => logger.security(event, context);
